import React, { useState } from 'react';
import './ListingStep.css';

function ListingStep({ onSubmit, initialData = {}, onPreviousStep, draftId }) {
  const [wantList, setWantList] = useState(
    typeof initialData.create_listing === 'boolean' ? initialData.create_listing : null
  );
  const [listingType, setListingType] = useState(
    initialData.listing_type ? initialData.listing_type.toUpperCase() : null
  );

  // available_from — default to today for non-tenant cases
  const todayISO = new Date().toISOString().split('T')[0];
  const [availableFrom, setAvailableFrom] = useState(
    initialData.available_from || ''
  );

  // Rent fields
  const [rentYears, setRentYears] = useState(
    initialData.lease_term_months ? Math.floor(initialData.lease_term_months / 12) : ''
  );
  const [furnishedStatus, setFurnishedStatus] = useState(initialData.furnished_status || 'UNFURNISHED');
  const [suitableTenants, setSuitableTenants] = useState(initialData.suitable_tenants || []);

  // Sale fields
  const [mortgageStatus, setMortgageStatus] = useState(initialData.mortgage_status || 'NONE');
  const [mortgageTotalAmount, setMortgageTotalAmount] = useState(
    initialData.mortgage_total_amount != null ? String(initialData.mortgage_total_amount) : ''
  );
  const [mortgageRepayPeriod, setMortgageRepayPeriod] = useState(
    initialData.mortgage_repay_period != null ? String(initialData.mortgage_repay_period) : ''
  );
  const [mortgagePayPerMonth, setMortgagePayPerMonth] = useState(
    initialData.mortgage_pay_per_month != null ? String(initialData.mortgage_pay_per_month) : ''
  );
  const [hasTenant, setHasTenant] = useState(
    typeof initialData.has_tenant === 'boolean' ? initialData.has_tenant : false
  );
  const [tenantDeposit, setTenantDeposit] = useState(
    initialData.tenant_deposit != null ? String(initialData.tenant_deposit) : ''
  );
  const [tenantMonthlyRent, setTenantMonthlyRent] = useState(
    initialData.tenant_monthly_rent != null ? String(initialData.tenant_monthly_rent) : ''
  );

  const [error, setError] = useState(null);

  const toggleTenant = (tenantType) => {
    setSuitableTenants(prev =>
      prev.includes(tenantType) ? prev.filter(t => t !== tenantType) : [...prev, tenantType]
    );
  };

  const handleContinue = () => {
    setError(null);
    if (wantList === null) { setError('لطفاً مشخص کنید آیا می‌خواهید ملک را آگهی کنید یا خیر'); return; }
    if (wantList === true) {
      if (!listingType) { setError('لطفاً نوع آگهی را انتخاب کنید'); return; }
      if (!availableFrom) { setError('لطفاً تاریخ در دسترس بودن ملک را وارد کنید'); return; }
      if (listingType === 'RENT') {
        const yearsNum = parseInt(rentYears, 10);
        if (!rentYears || Number.isNaN(yearsNum) || yearsNum <= 0) { setError('لطفاً مدت اجاره را وارد کنید'); return; }
      }
    }

    const payload = {
      create_listing: wantList,
      listing_type: wantList ? listingType : null,
      available_from: wantList ? availableFrom : null,
    };

    if (wantList && listingType === 'RENT') {
      payload.rent_details = {
        lease_term_months: parseInt(rentYears, 10) * 12,
        furnished_status: furnishedStatus,
        suitable_tenants: suitableTenants,
      };
    }

    if (wantList && listingType === 'SALE') {
      payload.sale_details = { mortgage_status: mortgageStatus, has_tenant: hasTenant };
      if (mortgageStatus === 'AVAILABLE') {
        payload.sale_details.mortgage_total_amount = mortgageTotalAmount ? parseFloat(mortgageTotalAmount) : null;
        payload.sale_details.mortgage_repay_period = mortgageRepayPeriod ? parseInt(mortgageRepayPeriod, 10) : null;
        payload.sale_details.mortgage_pay_per_month = mortgagePayPerMonth ? parseFloat(mortgagePayPerMonth) : null;
      }
      if (hasTenant) {
        payload.sale_details.tenant_deposit = tenantDeposit ? parseFloat(tenantDeposit) : null;
        payload.sale_details.tenant_monthly_rent = tenantMonthlyRent ? parseFloat(tenantMonthlyRent) : null;
      }
    }

    onSubmit(payload);
  };

  const tenantOptions = ['خانواده', 'دانشجو', 'تجاری', 'اداری', 'مجرد مرد', 'مجرد زن', 'زوج با فرزند', 'زوج بدون فرزند'];

  return (
    <div className="listing-step">
      {/* Header */}
      <div className="step-header">
        <h3>انتشار آگهی</h3>
        <p>آیا می‌خواهید ملک خود را در سایت آگهی کنید؟</p>
      </div>

      {error && <div className="ls-error">{error}</div>}

      <div className="ls-body">

        {/* Want to list? */}
        <div className="ls-card">
          <p className="ls-card-label">آیا قصد آگهی کردن دارید؟</p>
          <div className="ls-big-choice">
            <button
              type="button"
              className={`ls-big-btn ${wantList === true ? 'active green' : ''}`}
              onClick={() => setWantList(true)}
            >
              <span className="ls-big-btn-text">بله، می‌خواهم آگهی کنم</span>
            </button>
            <button
              type="button"
              className={`ls-big-btn ${wantList === false ? 'active gray' : ''}`}
              onClick={() => setWantList(false)}
            >
              <span className="ls-big-btn-text">خیر، فقط ثبت می‌کنم</span>
            </button>
          </div>
        </div>

        {/* No listing info */}
        {wantList === false && (
          <div className="ls-info-banner">
            ملک شما ثبت می‌شود. در صورت تمایل بعداً می‌توانید آگهی منتشر کنید.
          </div>
        )}

        {/* Listing type */}
        {wantList === true && (
          <>
            <div className="ls-card">
              <p className="ls-card-label">نوع آگهی</p>
              <div className="ls-type-choice">
                <button
                  type="button"
                  className={`ls-type-btn ${listingType === 'SALE' ? 'active' : ''}`}
                  onClick={() => setListingType('SALE')}
                >
                  <span className="ls-type-title">فروش</span>
                  <span className="ls-type-sub">انتقال مالکیت</span>
                </button>
                <button
                  type="button"
                  className={`ls-type-btn ${listingType === 'RENT' ? 'active' : ''}`}
                  onClick={() => setListingType('RENT')}
                >
                  <span className="ls-type-title">اجاره</span>
                  <span className="ls-type-sub">رهن و اجاره</span>
                </button>
              </div>
            </div>

            {/* ── AVAILABLE FROM ── */}
            <div className="ls-card">
              <p className="ls-card-label">تاریخ در دسترس بودن ملک</p>
              <p className="ls-card-hint">
                {listingType === 'SALE' && hasTenant
                  ? 'ملک مستاجر دارد — از چه تاریخی قابل تحویل به خریدار است؟'
                  : 'از چه تاریخی ملک قابل تحویل است؟'}
              </p>
              <div className="ls-available-from">
                <button
                  type="button"
                  className={`ls-avail-btn ${availableFrom === todayISO ? 'active' : ''}`}
                  onClick={() => setAvailableFrom(todayISO)}
                >
                  از همین امروز
                </button>
                <div className="ls-avail-custom">
                  <label className="ls-label">تاریخ دلخواه</label>
                  <input
                    type="date"
                    value={availableFrom}
                    min={todayISO}
                    onChange={e => setAvailableFrom(e.target.value)}
                    className="ls-input"
                    aria-label="تاریخ در دسترس بودن ملک"
                  />
                </div>
              </div>
            </div>

            {/* ── RENT DETAILS ── */}
            {listingType === 'RENT' && (
              <>
                {/* <div className="ls-ai-banner">
                  <span>قیمت‌ها (اجاره و رهن) در مرحله بعد با کمک هوش مصنوعی پیشنهاد می‌شوند</span>
                </div> */}

                {/* Lease term */}
                <div className="ls-card">
                  <p className="ls-card-label">مدت اجاره</p>
                  <div className="ls-year-selector">
                    {[1, 2, 3, 4, 5].map(y => (
                      <button
                        key={y}
                        type="button"
                        className={`ls-year-btn ${parseInt(rentYears, 10) === y ? 'active' : ''}`}
                        onClick={() => setRentYears(y)}
                      >
                        {y} سال
                      </button>
                    ))}
                    <div className="ls-year-custom">
                      <input
                        type="number"
                        min="1"
                        value={rentYears}
                        onChange={e => setRentYears(e.target.value)}
                        placeholder="سفارشی"
                        className="ls-input"
                        aria-label="مدت اجاره به سال"
                      />
                    </div>
                  </div>
                </div>

                {/* Furnished status */}
                <div className="ls-card">
                  <p className="ls-card-label">وضعیت مبلمان</p>
                  <div className="ls-furnished-grid">
                    {[
                      { value: 'UNFURNISHED', label: 'بدون مبلمان' },
                      { value: 'SEMI_FURNISHED', label: 'نیمه مبله' },
                      { value: 'FULLY_FURNISHED', label: 'کاملاً مبله' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`ls-furnished-btn ${furnishedStatus === opt.value ? 'active' : ''}`}
                        onClick={() => setFurnishedStatus(opt.value)}
                      >
                        <span>{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Suitable tenants */}
                <div className="ls-card">
                  <p className="ls-card-label">
                    مستاجرین مناسب
                    {suitableTenants.length > 0 && (
                      <span className="ls-badge">{suitableTenants.length} انتخاب شده</span>
                    )}
                  </p>
                  <div className="ls-tenant-grid">
                    {tenantOptions.map(option => (
                      <button
                        key={option}
                        type="button"
                        className={`ls-tenant-btn ${suitableTenants.includes(option) ? 'active' : ''}`}
                        onClick={() => toggleTenant(option)}
                        aria-pressed={suitableTenants.includes(option)}
                      >
                        <span>{option}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* ── SALE DETAILS ── */}
            {listingType === 'SALE' && (
              <>
                {/* Mortgage */}
                <div className="ls-card">
                  <p className="ls-card-label">وام قابل انتقال</p>
                  <div className="ls-toggle-pair">
                    <button
                      type="button"
                      className={`ls-toggle-btn ${mortgageStatus === 'NONE' ? 'active' : ''}`}
                      onClick={() => setMortgageStatus('NONE')}
                    >
                      ندارد
                    </button>
                    <button
                      type="button"
                      className={`ls-toggle-btn ${mortgageStatus === 'AVAILABLE' ? 'active' : ''}`}
                      onClick={() => setMortgageStatus('AVAILABLE')}
                    >
                      دارد
                    </button>
                  </div>

                  {mortgageStatus === 'AVAILABLE' && (
                    <div className="ls-sub-fields">
                      <div className="ls-field">
                        <label className="ls-label">مبلغ کل وام (ریال)</label>
                        <input
                          type="number"
                          min="0"
                          value={mortgageTotalAmount}
                          onChange={e => setMortgageTotalAmount(e.target.value)}
                          className="ls-input"
                          placeholder="مثلاً ۵۰۰۰۰۰۰۰۰"
                        />
                      </div>
                      <div className="ls-fields-row">
                        <div className="ls-field">
                          <label className="ls-label">مدت بازپرداخت (ماه)</label>
                          <input
                            type="number"
                            min="1"
                            value={mortgageRepayPeriod}
                            onChange={e => setMortgageRepayPeriod(e.target.value)}
                            className="ls-input"
                            placeholder="تعداد ماه"
                          />
                        </div>
                        <div className="ls-field">
                          <label className="ls-label">قسط ماهانه (ریال)</label>
                          <input
                            type="number"
                            min="0"
                            value={mortgagePayPerMonth}
                            onChange={e => setMortgagePayPerMonth(e.target.value)}
                            className="ls-input"
                            placeholder="مبلغ قسط"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Has tenant */}
                <div className="ls-card">
                  <p className="ls-card-label">وضعیت سکونت</p>
                  <div className="ls-toggle-pair">
                    <button
                      type="button"
                      className={`ls-toggle-btn ${!hasTenant ? 'active' : ''}`}
                      onClick={() => setHasTenant(false)}
                    >
                      ملک خالی است
                    </button>
                    <button
                      type="button"
                      className={`ls-toggle-btn ${hasTenant ? 'active' : ''}`}
                      onClick={() => setHasTenant(true)}
                    >
                      مستاجر دارد
                    </button>
                  </div>

                  {hasTenant && (
                    <div className="ls-sub-fields">
                      <div className="ls-fields-row">
                        <div className="ls-field">
                          <label className="ls-label">مبلغ رهن مستاجر فعلی (ریال)</label>
                          <input
                            type="number"
                            min="0"
                            value={tenantDeposit}
                            onChange={e => setTenantDeposit(e.target.value)}
                            className="ls-input"
                            placeholder="مبلغ رهن"
                          />
                        </div>
                        <div className="ls-field">
                          <label className="ls-label">اجاره ماهانه مستاجر فعلی (ریال)</label>
                          <input
                            type="number"
                            min="0"
                            value={tenantMonthlyRent}
                            onChange={e => setTenantMonthlyRent(e.target.value)}
                            className="ls-input"
                            placeholder="مبلغ اجاره"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Navigation */}
      <div className="navigation-buttons">
        <button type="button" onClick={handleContinue} className="submit-button">
          تأیید و ادامه
        </button>
        <button type="button" onClick={onPreviousStep} className="previous-button">
          بازگشت
        </button>
      </div>
    </div>
  );
}

export default ListingStep;
